import React from 'react';
import {Box, Typography} from '@mui/material';

/** Default digit cell count (matches ReadExcelServiceImpl.PTR_DIGIT_BOX_COUNT / PTR.xlsx AG-AR). */
export const DIGIT_BOX_COUNT = 12;

export type DigitAmountBoxesProps = {
    value: unknown;
    /** Shown left of the digit boxes (e.g. presentationCurrency / HKD). Omit to hide. */
    currency?: string;
    /** Override box count; default matches PTR.xlsx AG-AR (12). Ignored when filledOnly (unless blank). */
    boxCount?: number;
    /** Only render cells that contain a digit (no empty pad boxes). */
    filledOnly?: boolean;
    /** When value is empty, still render boxCount empty cells (e.g. Reference No.). */
    blankWhenEmpty?: boolean;
    /** Tighter boxes for dual-column PTR layouts (avoid horizontal scroll). */
    dense?: boolean;
};

/** Mirror ReadExcelServiceImpl.normalizePtrDigitString for preview boxes. */
export function normalizeDigitString(value: unknown): string {
    if (value === null || value === undefined) return '0';
    const cleaned = String(value).trim().replace(/,/g, '');
    if (!cleaned) return '0';
    if (/^0\d+$/.test(cleaned)) return cleaned;
    if (/^-?\d+(\.\d+)?$/.test(cleaned)) {
        const n = Math.abs(Math.round(Number(cleaned)));
        return Number.isFinite(n) ? String(n) : '0';
    }
    return cleaned;
}

/** Right-align digits into Excel-style cells (trailing '0' alone when zero). */
export function toDigitCells(digits: string, boxCount: number = DIGIT_BOX_COUNT): string[] {
    const cells = Array.from({length: boxCount}, () => '');
    if (digits === '0') {
        cells[boxCount - 1] = '0';
        return cells;
    }
    const chars = digits.split('');
    let col = boxCount - 1;
    for (let i = chars.length - 1; i >= 0 && col >= 0; i -= 1, col -= 1) {
        cells[col] = chars[i];
    }
    return cells;
}

/**
 * Read-only amount: optional currency prefix + one digit per bordered cell.
 * Used by Tax Return preview (and reusable for other digit-box amounts).
 */
export function DigitAmountBoxes({
    value,
    currency,
    boxCount = DIGIT_BOX_COUNT,
    filledOnly = false,
    blankWhenEmpty = false,
    dense = false,
}: DigitAmountBoxesProps) {
    const rawDigits = String(value ?? '').trim().replace(/,/g, '');
    const emptyCells = () => Array.from({length: boxCount}, () => '');
    let cells: string[];
    if (!rawDigits && blankWhenEmpty) {
        cells = emptyCells();
    } else if (filledOnly) {
        // filledOnly: raw digits only (no empty pads; empty → no boxes unless blankWhenEmpty)
        cells = rawDigits ? rawDigits.split('') : [];
    } else {
        cells = toDigitCells(normalizeDigitString(value), boxCount);
    }
    const cellW = dense ? 11 : 15;
    const cellH = dense ? 18 : 22;
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: dense ? 0.5 : 0.75,
                maxWidth: '100%',
            }}
        >
            {currency ? (
                <Typography
                    component="span"
                    variant="caption"
                    sx={{
                        fontWeight: 700,
                        minWidth: dense ? 22 : 28,
                        textAlign: 'right',
                        flexShrink: 0,
                        fontSize: dense ? 11 : undefined,
                    }}
                >
                    {currency}
                </Typography>
            ) : null}
            <Box sx={{display: 'inline-flex', gap: dense ? '1px' : '2px', flexShrink: 1, minWidth: 0}}>
                {cells.map((digit, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            width: cellW,
                            height: cellH,
                            border: '1px solid',
                            borderColor: 'text.primary',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: dense ? 11 : 12,
                            lineHeight: 1,
                            fontVariantNumeric: 'tabular-nums',
                            bgcolor: 'background.paper',
                            flexShrink: 0,
                        }}
                    >
                        {digit}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
